import React, { createContext, useContext, useState } from 'react';
import { CloudData } from '../../data/cloud-data.js';
import { Configuration } from '../../data/configuration.js';
import { ExteriorConfigurator } from '../../configurator/exterior-configurator.js';
import { VehicleView } from '../vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from '../view-content.js';
import SelectedSectionItem from '../ui/selected-section-item.tsx';
import ViewContentRows from '../ui/view-content-rows.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface CowlViewProps {
	exteriorConfigurator: ExteriorConfigurator;
	onBackButtonClicked: () => void;
}

interface CowlViewContextType {
	selectedStoneGuardItemId: string;
	handleStoneGuardItemClicked: (id: string) => void;
	selectedGrilleMeshItemId: string;
	handleGrilleMeshItemClicked: (id: string) => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function CowlView(props: CowlViewProps) {

	// initial values.
	const initialStoneGuardItemId = Configuration.stoneGuardOption;
	const initialStoneGuardItemName = () => getItemName(initialStoneGuardItemId);
	const initialGrilleMeshItemId = Configuration.grilleMeshOption;
	const initialGrilleMeshItemName = () => getGrilleMeshItemName(initialGrilleMeshItemId);

	// states.
	const [selectedStoneGuardItemId, setSelectedStoneGuardItemId] = useState(initialStoneGuardItemId);
	const [selectedStoneGuardItemName, setSelectedStoneGuardItemName] = useState(initialStoneGuardItemName);
	const [selectedGrilleMeshItemId, setSelectedGrilleMeshItemId] = useState(initialGrilleMeshItemId);
	const [selectedGrilleMeshItemName, setSelectedGrilleMeshItemName] = useState(initialGrilleMeshItemName);
	
	// handlers.

	const handleStoneGuardItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedStoneGuardItemId(id);
		setSelectedStoneGuardItemName(getItemName(id));

		// update the configuration.
		Configuration.stoneGuardOption = id;
		Configuration.updateCosts();

		// select the stone guardstyle on the car.
		props.exteriorConfigurator.selectStoneGuardStyle(id);
	};

	const handleGrilleMeshItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedGrilleMeshItemId(id);
		setSelectedGrilleMeshItemName(getGrilleMeshItemName(id));

		// update the configuration.
		Configuration.grilleMeshOption = id;
		Configuration.updateCosts();

		// select the grille mesh style on the car.
		props.exteriorConfigurator.selectGrilleMeshOption(id);
	};

	// return the HTML.
	return (
		<CowlViewContext.Provider value={{ selectedStoneGuardItemId, handleStoneGuardItemClicked, selectedGrilleMeshItemId, handleGrilleMeshItemClicked }}>
			<VehicleView
				onNextButtonClicked={props.onBackButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-cowl">
							COWL
						</h2>
					</div>

					<SectionPanel
						subHeading={'STONE GUARD'}
						modalContent={CloudData.getModalContent(selectedStoneGuardItemId, CloudData.stoneGuardOptions)}
					>
						<SelectedSectionItem
							itemName={selectedStoneGuardItemName}
							sectionPrice={CloudData.getPrice(selectedStoneGuardItemId, CloudData.stoneGuardOptions, { ref: false })}
						/>
						<StoneGuardItems />
					</SectionPanel>

					<SectionPanel
						subHeading={'GRILLE MESH'}
						modalContent={CloudData.getModalContent(selectedGrilleMeshItemId, CloudData.grilleMeshOptions)}
					>
						<SelectedSectionItem
							itemName={selectedGrilleMeshItemName}
							sectionPrice={CloudData.getPrice(selectedGrilleMeshItemId, CloudData.grilleMeshOptions, { ref: false })}
						/>
						<GrilleMeshItems />
					</SectionPanel>

				</div>
			</VehicleView>
		</CowlViewContext.Provider>
	);
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

// context to allow any view in the hierarchy to access the selected item id and call the item clicked handler.
const CowlViewContext = createContext<CowlViewContextType | undefined>(undefined);

function StoneGuardItems() {
	return (
		<ViewContentRows>
			<StoneGuardItem id={'COWL001'} imageFilePath={'polished'} />
			<StoneGuardItem id={'COWL002'} imageFilePath={'black'} />
		</ViewContentRows>
	);
};

function StoneGuardItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(CowlViewContext);
	if (!context) {
		throw new Error('CowlViewContext must be used within a CowlView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/exterior/stone-guard/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedStoneGuardItemId}
			onItemClicked={context.handleStoneGuardItemClicked}
		/>
	);
}

function GrilleMeshItems() {
	return (
		<ViewContentRows maxRowLength={1}>
			<div className="view-content-row">
				<GrilleMeshItem id={'COWL003'} imageFilePath={'polished'} />
				<GrilleMeshItem id={'COWL004'} imageFilePath={'black'} />
			</div>
			<div className="view-content-row">
				<GrilleMeshItem id={'COWL015'} imageFilePath={'0'} />
				<GrilleMeshItem id={'COWL016'} imageFilePath={'1'} />
				<GrilleMeshItem id={'COWL017'} imageFilePath={'2'} />
				<GrilleMeshItem id={'COWL018'} imageFilePath={'3'} />
				<GrilleMeshItem id={'COWL019'} imageFilePath={'4'} />
				<GrilleMeshItem id={'COWL020'} imageFilePath={'5'} />
				<GrilleMeshItem id={'COWL021'} imageFilePath={'6'} />
				<GrilleMeshItem id={'COWL022'} imageFilePath={'7'} />
				<GrilleMeshItem id={'COWL023'} imageFilePath={'8'} />
				<GrilleMeshItem id={'COWL024'} imageFilePath={'9'} />
			</div>
		</ViewContentRows>
	);
};

function GrilleMeshItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(CowlViewContext);
	if (!context) {
		throw new Error('CowlViewContext must be used within a CowlView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getGrilleMeshItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/exterior/grille-mesh/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedGrilleMeshItemId}
			onItemClicked={context.handleGrilleMeshItemClicked}
		/>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getItemName(id: string): string {
	return CloudData.getName(id, CloudData.stoneGuardOptions);
}

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getGrilleMeshItemName(id: string): string {
	return CloudData.getName(id, CloudData.grilleMeshOptions);
}
